import * as React from 'react'
import { buildSections, GalleryItemLike, GalleryLayout, GalleryLayoutMode } from './buildSections'
import { computeAnchorDelta } from './anchorScroll'
import { flattenRows, scrollTargetY, visibleRangeForViewport, VisibleRange } from './visibleRange'

export interface GalleryScrollTarget {
  /** index into the photos array (window-relative) */
  index: number
  token: number
}

interface UseJustifiedGalleryOptions<T extends GalleryItemLike> {
  photos: Array<T>
  containerRef: React.RefObject<HTMLElement | null>
  groupByDate?: boolean
  layoutMode?: GalleryLayoutMode
  onVisibleRangeChange?: (range: VisibleRange) => void
  scrollTarget?: GalleryScrollTarget | null
  onScrollTargetApplied?: () => void
}

interface UseJustifiedGalleryResult<T extends GalleryItemLike> {
  layout: GalleryLayout<T>
  containerWidth: number
}

/** fixed navbar (64px) + breathing room */
const SCROLL_OFFSET_TOP = 72

export const useJustifiedGallery = <T extends GalleryItemLike>({
  photos,
  containerRef,
  groupByDate = true,
  layoutMode = 'justified',
  onVisibleRangeChange,
  scrollTarget,
  onScrollTargetApplied,
}: UseJustifiedGalleryOptions<T>): UseJustifiedGalleryResult<T> => {
  const [containerWidth, setContainerWidth] = React.useState(0)

  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const apply = (width: number) => {
      const rounded = Math.round(width)
      // Integer-width guard: our layout only changes height, so reacting to sub-pixel
      // width reports would loop the observer for nothing.
      setContainerWidth((prev) => (prev === rounded ? prev : rounded))
    }

    apply(el.getBoundingClientRect().width)

    let frame = 0
    const observer = new ResizeObserver((entries) => {
      const width = entries[entries.length - 1].contentRect.width
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => apply(width))
    })
    observer.observe(el)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [containerRef])

  const layout = React.useMemo(
    () => buildSections(photos, containerWidth, { groupByDate, layoutMode }),
    [photos, containerWidth, groupByDate, layoutMode]
  )
  const rows = React.useMemo(() => flattenRows(layout), [layout])

  const containerTopDoc = (): number | null => {
    const el = containerRef.current
    if (!el) return null
    return el.getBoundingClientRect().top + window.scrollY
  }

  // Prepend / top-trim compensation: same anchor photo, exact layout-diff delta.
  const anchorRef = React.useRef<{ layout: GalleryLayout<T>; firstId: string | null }>({
    layout,
    firstId: photos[0]?.id ?? null,
  })
  React.useLayoutEffect(() => {
    const prev = anchorRef.current
    anchorRef.current = { layout, firstId: photos[0]?.id ?? null }

    const firstId = photos[0]?.id ?? null
    if (!prev.firstId || !firstId || prev.firstId === firstId) return

    // anchor on whichever first item survives the change
    const anchorId = prev.firstId
    const delta =
      computeAnchorDelta(prev.layout, layout, anchorId) ??
      computeAnchorDelta(prev.layout, layout, firstId)
    if (delta) window.scrollBy(0, delta)
  }, [layout, photos])

  // rAF-throttled visible-range reporting from window scroll
  const rowsRef = React.useRef(rows)
  rowsRef.current = rows
  const rangeCallbackRef = React.useRef(onVisibleRangeChange)
  rangeCallbackRef.current = onVisibleRangeChange

  React.useEffect(() => {
    if (!onVisibleRangeChange) return

    let frame = 0
    const report = () => {
      const top = containerTopDoc()
      if (top === null) return
      const localTop = window.scrollY - top
      const range = visibleRangeForViewport(
        rowsRef.current,
        localTop,
        localTop + window.innerHeight
      )
      if (range) rangeCallbackRef.current?.(range)
    }
    const onScroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(report)
    }

    report()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
    }
    // rows change is covered via rowsRef; re-run only when the callback presence flips
  }, [Boolean(onVisibleRangeChange)])

  // Jump target: applied once per token, as soon as the index is inside the loaded window
  const appliedTokenRef = React.useRef(0)
  React.useLayoutEffect(() => {
    if (!scrollTarget || appliedTokenRef.current === scrollTarget.token) return
    if (scrollTarget.index < 0 || scrollTarget.index >= photos.length) return

    const y = scrollTargetY(layout, scrollTarget.index)
    const top = containerTopDoc()
    if (y === null || top === null) return

    appliedTokenRef.current = scrollTarget.token
    window.scrollTo({ top: Math.max(0, top + y - SCROLL_OFFSET_TOP) })
    onScrollTargetApplied?.()
  }, [scrollTarget, layout, photos.length])

  return { layout, containerWidth }
}
